"use client";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Counter, Dialog, type DialogProps, Input, cn } from "@tosize/ui";
import { filter } from "fuzzy";
import { useSearchParams } from "next/navigation";
import { Accordion } from "radix-ui";
import { type ChangeEvent, useDeferredValue, useMemo, useState } from "react";
import type {
	Aggregation,
	AggregationOption,
	ProductsQuery,
} from "~/app/[lang]/(default)/category/[id]/ProductsGrid";
import {
	FILTER_SEPARATOR,
	useFilterOption,
} from "~/app/[lang]/(default)/category/[id]/ProductsGrid/Filter/useFilterOption";
import { StyledLink } from "~/components/Link";
import { useQuery } from "~/hooks/useQuery";
import { useRouter } from "~/hooks/useRouter";
import { FilterItemOption, type FilterItemOptionProps } from "./FilterItemOption";

interface Messages {
	explanationMessage: string;
	productCountMessage: string;
	searchLabelMessage: string;
	emptyResultMessage: string;
}

interface FilterOptionsProps {
	children: React.ReactNode;
	aggregations: ProductsQuery["aggregations"];
	messages: Messages;
}

export function FilterOptions({ children, aggregations, messages }: FilterOptionsProps) {
	const searchParams = useSearchParams();

	const activeFilters = aggregations.filter((aggregation) =>
		searchParams?.has(aggregation.attribute_code),
	);

	return (
		<Accordion.Root
			defaultValue={activeFilters.map((filter) => filter.attribute_code)}
			type="multiple"
			className="child:border-shades child:border-b last:child:border-b-0"
		>
			{children}

			{aggregations.map((aggregation) => (
				<FilterOption
					key={aggregation.attribute_code}
					aggregation={aggregation}
					messages={messages}
				/>
			))}
		</Accordion.Root>
	);
}

interface AccordionItemProps {
	value: string;
	title: string;
	count?: number;
	header?: React.ReactNode;
	className?: string;
	children: React.ReactNode;
}

export function AccordionFilterItem(props: AccordionItemProps) {
	const { value, title, count, header, className, children } = props;

	return (
		<Accordion.Item value={value} className={className}>
			<Accordion.Header className="flex items-center">
				<Accordion.Trigger className="group flex w-full items-center py-4 focus:outline-none">
					<div className="group-rdx-state-open:rotate-180">
						<FontAwesomeIcon icon={faChevronDown} />
					</div>
					<div className="mx-2 text-left font-bold group-hover:underline group-focus-visible:underline">
						{title}
					</div>
					{!!count && <Counter amount={count} color="secondary" />}
				</Accordion.Trigger>

				{header}
			</Accordion.Header>
			<Accordion.Content>{children}</Accordion.Content>
		</Accordion.Item>
	);
}

interface FilterOptionProps {
	aggregation: Aggregation;
	messages: Messages;
}

function FilterOption({ aggregation, messages }: FilterOptionProps) {
	const { createUrl } = useQuery();

	const searchParams = useSearchParams();
	const count = searchParams?.get(aggregation.attribute_code)?.split(FILTER_SEPARATOR).length ?? 0;

	return (
		<AccordionFilterItem
			value={aggregation.attribute_code}
			title={aggregation.label}
			count={count}
			header={
				aggregation.hasDescription && (
					<StyledLink
						href={createUrl({
							explanation: aggregation.attribute_code,
							utm_source: "categorypage",
							utm_medium: "filter",
							utm_campaign: "crs",
							utm_id: "uitleg",
							utm_content: aggregation.attribute_code,
						})}
					>
						{messages.explanationMessage}
					</StyledLink>
				)
			}
		>
			<FilterOptionList aggregation={aggregation} messages={messages} />
		</AccordionFilterItem>
	);
}

export type RenderMode = "radio" | "check" | "grid";

export function FilterOptionList({
	aggregation,
	messages,
}: {
	aggregation: Aggregation;
	messages: Messages;
}) {
	const [search, setSearch] = useState("");
	const query = useDeferredValue(search);
	const renderMode: RenderMode = aggregation.attribute_code === "thickness" ? "grid" : "check";

	function onSearchChange(evt: ChangeEvent<HTMLInputElement>) {
		setSearch(evt.target.value);
	}

	const options = useMemo(() => {
		const filtered = filter(query, aggregation.options, {
			pre: "<b>",
			post: "</b>",
			extract: (option) => option.label ?? "",
		});

		return filtered.map((result) => ({
			...(aggregation.options[result.index] as AggregationOption),
			label: result.string,
		}));
	}, [query, aggregation.options]);

	return (
		<div className="flex flex-col space-y-4">
			{renderMode === "check" && (
				<Input
					placeholder={messages.searchLabelMessage}
					aria-label={messages.searchLabelMessage}
					type="text"
					value={search}
					onChange={onSearchChange}
				/>
			)}

			{options.length === 0 ? (
				<div className="py-2">{messages.emptyResultMessage}</div>
			) : (
				<div
					className={cn({
						"grid grid-cols-4 gap-4 pb-4": renderMode === "grid",
					})}
				>
					{options.map((option) => (
						<FilterItemOptionWrapper
							key={option.value}
							code={aggregation.attribute_code}
							value={option.value}
							title={option.label}
							count={option.count}
							mode={renderMode}
						/>
					))}
				</div>
			)}
		</div>
	);
}

interface FilterItemOptionWrapperProps
	extends Pick<FilterItemOptionProps, "title" | "count" | "mode"> {
	code: string;
	value: string;
}

function FilterItemOptionWrapper({ code, value, ...remainingProps }: FilterItemOptionWrapperProps) {
	const { isActive, href } = useFilterOption({ code, value });

	return <FilterItemOption href={href} isActive={isActive} {...remainingProps} />;
}

interface FilterOptionDialogProps extends Omit<DialogProps, "open" | "onOpenChange"> {
	attribute_code?: string;
	anchor?: string;
	children?: React.ReactNode;
}

export function FilterOptionDialog({
	attribute_code,
	anchor = "",
	...props
}: FilterOptionDialogProps) {
	const router = useRouter();
	const { createUrl } = useQuery();

	return (
		<Dialog
			defaultOpen={attribute_code !== undefined}
			open={attribute_code !== undefined}
			onOpenChange={() => {
				const url = createUrl({ explanation: null }) + anchor;

				router.replace(url);
			}}
			{...props}
		/>
	);
}
